import { isBreakdownReport } from './reportTypeUtils';

/**
 * Value transformations between UI display values and API/database values.
 * 
 * UI displays human-readable values like "Meta Ads", "Reach & Frequency"
 * API/DB stores snake_case values like "meta_ads", "reach_and_frequency"
 */

export const UI_TO_API: Record<string, string> = {
  'Meta Ads': 'meta_ads',
  'Google Ads': 'google_ads',
  'TikTok Ads': 'tiktok_ads',
  'Reach & Frequency': 'reach_and_frequency',
  'Age & Gender': 'age_and_gender',
  'DMA': 'dma',
  'SKU': 'sku',
  'Prior Week': 'prior_week',
  'Prior Month': 'prior_month',
  'Month-to-date': 'month_to_date',
  '7 Days Lookback': '7_days_lookback',
  '30 Days Lookback': '30_days_lookback',
  'Campaign': 'campaign',
  'Ad Set': 'ad_set',
  'Ad': 'ad',
  'Delivery': 'delivery',
  'Conversions': 'conversions',
};

export const API_TO_UI: Record<string, string> = Object.fromEntries(
  Object.entries(UI_TO_API).map(([key, value]) => [value, key])
);

/**
 * Transform a UI display value to API format
 * Falls back to snake_case conversion if not in mapping
 * Returns the original value for non-null/undefined inputs
 */
export const transformToAPI = (value: string | null | undefined): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (UI_TO_API[value]) return UI_TO_API[value];
  
  return value
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/&/g, 'and');
};

/**
 * Transform an API value to UI display format
 * Falls back to title case conversion if not in mapping
 */
export const transformToUI = (value: string | null | undefined): string => {
  if (!value || value === 'n/a') return 'N/A';
  if (API_TO_UI[value]) return API_TO_UI[value];
  
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replace(/And/g, '&');
};

/**
 * Transform form data from UI values to API payload
 */
export const transformFormDataToAPI = (formData: {
  platform: string | null;
  report_type: string | null;
  granularity_level: string;
  metric_type: string | null;
  cadence: string | null;
  breakdown: string | null;
}): {
  platform: string | null;
  report_type: string | null;
  granularity_level: string | null;
  metric_type: string | null;
  cadence: string | null;
  breakdown: string | null;
} => {
  const reportTypeAPI = transformToAPI(formData.report_type);
  const isBreakdown = isBreakdownReport(reportTypeAPI);
  
  return {
    platform: transformToAPI(formData.platform),
    report_type: reportTypeAPI,
    granularity_level: transformToAPI(formData.granularity_level),
    metric_type: isBreakdown ? transformToAPI(formData.metric_type) : null,
    cadence: isBreakdown ? null : transformToAPI(formData.cadence),
    breakdown: isBreakdown ? reportTypeAPI : null,
  };
};

/**
 * Transform API response to UI display values
 */
export const transformConfigToUI = (config: {
  platform: string;
  report_type: string;
  granularity_level: string;
  metric_type: string;
  cadence: string;
  breakdown: string | null;
}): {
  platform: string;
  report_type: string;
  granularity_level: string;
  metric_type: string;
  cadence: string;
  breakdown: string | null;
} => {
  return {
    platform: transformToUI(config.platform),
    report_type: transformToUI(config.report_type),
    granularity_level: transformToUI(config.granularity_level),
    metric_type: transformToUI(config.metric_type),
    cadence: transformToUI(config.cadence),
    breakdown: config.breakdown ? transformToUI(config.breakdown) : null,
  };
};
